import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useAdminAuth, hasRole } from '../../auth';
import { useRange, withRange } from '../../range';
import { usePageParams, pageMeta } from '../../paging';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import Pagination from '../../components/Pagination';
import StatusPill from '../../components/StatusPill';
import { ReasonDialog } from '../../components/Dialog';
import { formatCompactNumber, formatPct } from '../../format';

/**
 * Catalog › Products — one table, sortable, searchable.
 *
 * This merges two pages that did the same job differently. "Catalog" was a
 * metrics table with a hide button; "Products" was a search box with the
 * same hide button. Both called the same `PATCH /ops/products/{sku}`, so an
 * operator had to know which of two pages to open based on whether they had
 * a SKU or a hunch.
 *
 * Search is now a filter on the one table rather than a separate page. When
 * a query is present the row set comes from `/ops/products` (which searches
 * the whole catalogue, including products with no engagement at all); with
 * no query it comes from `/catalog/products` (the engagement rollup, which
 * only knows about products someone actually saw). That distinction is
 * stated on screen, because "why isn't my product in this table" has exactly
 * that answer.
 */
const SORTABLE = new Set([
  'impressions', 'saves', 'passes', 'save_rate_pct', 'tryons',
  'tryon_completion_rate_pct', 'buy_clicks', 'billable_clicks',
]);

export default function ProductsPage() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const range = useRange();
  const canModerate = hasRole(admin, 'operator', 'owner');
  const [params, setParams] = useSearchParams();

  const brandId = params.get('brand') || '';
  const category = params.get('category') || '';
  const query = params.get('q') || '';
  const sortKey = params.get('sort') || 'impressions';
  const sortOrder = params.get('order') || 'desc';

  // Filters and sort reset the page through `setParams_` below rather than a
  // separate call, so the page number and the thing that invalidated it change
  // in one URL update.
  const { page, pageSize, setPage, setPageSize } = usePageParams();

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queryDraft, setQueryDraft] = useState(query);
  const [moderating, setModerating] = useState(null);

  useEffect(() => { setQueryDraft(query); }, [query]);

  /**
   * Applies several query-param changes in ONE update.
   *
   * Two sequential `setParams` calls in the same handler are not safe here:
   * the second reads a `prev` that does not yet include the first, so
   * changing sort column and direction together would drop one of them.
   * Everything that changes together goes through a single patch.
   */
  const setParams_ = useCallback((patch) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      return next;
    }, { replace: true });
  }, [setParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (query) {
        // Search hits the catalogue directly and is already capped by the
        // endpoint's own limit — a search that returns 20 rows does not need
        // a pager, and pretending otherwise would imply pages that aren't there.
        const { data } = await adminApi.get('/ops/products', { params: { q: query } });
        setRows((data.items || []).map((p) => ({ ...p, brand_name: p.brand, searchResult: true })));
        setMeta(null);
      } else {
        const { data } = await adminApi.get('/catalog/products', {
          params: {
            days: range.days,
            sort: SORTABLE.has(sortKey) ? sortKey : 'impressions',
            order: sortOrder,
            page,
            page_size: pageSize,
            ...(brandId ? { brand_id: brandId } : {}),
            ...(category ? { category } : {}),
          },
        });
        setRows(data.items || []);
        setMeta(pageMeta(data));
      }
    } catch (err) {
      toast.error(errMsg(err, 'Could not load products'));
    } finally {
      setLoading(false);
    }
  }, [query, range.days, sortKey, sortOrder, brandId, category, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const moderate = async (reason) => {
    const { product, hidden } = moderating;
    try {
      await adminApi.patch(`/ops/products/${encodeURIComponent(product.mira_sku)}`, { hidden, reason });
      setRows((prev) => prev.map((p) => (p.mira_sku === product.mira_sku ? { ...p, hidden } : p)));
      toast.success(hidden ? 'Product hidden from discovery' : 'Product re-enabled');
      setModerating(null);
    } catch (err) {
      toast.error(errMsg(err, 'Could not update product'));
      setModerating(null);
    }
  };

  // Re-sorting reorders the whole result, so the row you were looking at on
  // page 4 is not on page 4 any more — going back to the first page is the
  // only non-confusing behaviour. Same for changing a filter, below.
  const onSort = (key) => {
    if (!SORTABLE.has(key) || query) return;
    if (sortKey === key) setParams_({ order: sortOrder === 'desc' ? 'asc' : 'desc', page: null });
    else setParams_({ sort: key, order: 'desc', page: null });
  };

  const columns = useMemo(() => {
    const base = [
      {
        key: 'title',
        label: 'Product',
        width: '26%',
        render: (r) => (
          <div className="min-w-0">
            <div className="truncate font-medium text-ink" title={r.title}>{r.title || r.mira_sku}</div>
            <div className="truncate font-mono text-[10px] text-ink-muted">{r.mira_sku}</div>
          </div>
        ),
      },
      { key: 'brand_name', label: 'Brand', render: (r) => r.brand_name || r.brand },
    ];

    // A search result comes from the catalogue, not the engagement rollup, so
    // it genuinely has no metrics to show — rendering the columns as zeros
    // would claim nobody engaged with it, which is a different statement.
    const metrics = query ? [] : [
      { key: 'impressions', label: 'Impressions', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.impressions) },
      { key: 'saves', label: 'Saved', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.saves) },
      { key: 'passes', label: 'Passed', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.passes) },
      { key: 'save_rate_pct', label: 'Save rate', align: 'right', sortable: true, render: (r) => formatPct(r.save_rate_pct, 1) },
      { key: 'tryons', label: 'Try-ons', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.tryons) },
      { key: 'billable_clicks', label: 'Billable', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.billable_clicks) },
    ];

    const tail = [
      {
        key: 'hidden',
        label: 'Visibility',
        render: (r) => (r.hidden
          ? <StatusPill tone="critical" label="Hidden" />
          : <StatusPill tone="good" label="Visible" />),
      },
    ];

    if (canModerate) {
      tail.push({
        key: 'moderate',
        label: '',
        align: 'right',
        render: (r) => (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setModerating({ product: r, hidden: !r.hidden }); }}
            className={`rounded-md border px-2 py-1 text-[11px] font-medium ${
              r.hidden
                ? 'border-status-good/30 text-status-good hover:bg-status-good/10'
                : 'border-status-critical/30 text-status-critical hover:bg-status-critical/10'
            }`}
          >
            {r.hidden ? 'Re-enable' : 'Hide'}
          </button>
        ),
      });
    }

    return [...base, ...metrics, ...tail];
  }, [query, canModerate]);

  return (
    <div>
      <PageHeader
        title="Products"
        hint={query
          ? 'Searching the whole catalogue, including products with no engagement yet.'
          : `Products people actually saw — ${range.label.toLowerCase()}. Sorted by any column.`}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          onSubmit={(e) => { e.preventDefault(); setParams_({ q: queryDraft.trim(), page: null }); }}
          className="flex gap-2"
        >
          <input
            placeholder="Search by title, brand or SKU"
            value={queryDraft}
            onChange={(e) => setQueryDraft(e.target.value)}
            className="w-72 rounded-lg border border-hairline px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
          />
          <button type="submit" className="rounded-lg border border-hairline px-3 py-1.5 text-sm font-medium text-ink-secondary hover:bg-surface-page hover:text-ink">
            Search
          </button>
        </form>

        {!query && (
          <input
            placeholder="Filter by category"
            value={category}
            onChange={(e) => setParams_({ category: e.target.value, page: null })}
            className="w-52 rounded-lg border border-hairline px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
          />
        )}

        {(query || category || brandId) && (
          <button
            type="button"
            onClick={() => { setParams(new URLSearchParams(), { replace: true }); setQueryDraft(''); }}
            className="text-xs font-medium text-ink-secondary hover:text-ink"
          >
            Clear filters ✕
          </button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.mira_sku}
        loading={loading}
        sort={query ? null : { key: sortKey, order: sortOrder }}
        onSort={onSort}
        onRowClick={(r) => navigate(withRange(`/product/${encodeURIComponent(r.mira_sku)}`, range))}
        empty={
          <EmptyState
            title={query ? 'Nothing matched' : 'No engagement recorded in this window'}
            detail={query
              ? 'Search matches product titles, brand names and exact SKUs.'
              : 'Only products someone saw appear here. Search by name or SKU to reach a product with no activity yet.'}
          />
        }
      />

      <Pagination meta={meta} loading={loading} onPage={setPage} onPageSize={setPageSize} />

      <ReasonDialog
        open={Boolean(moderating)}
        tone={moderating?.hidden ? 'critical' : 'default'}
        title={moderating?.hidden
          ? `Hide "${moderating?.product?.title || moderating?.product?.mira_sku}"?`
          : `Re-enable "${moderating?.product?.title || moderating?.product?.mira_sku}"?`}
        body={moderating?.hidden
          ? 'It stops appearing in discovery, search and anything the stylist can recommend. People who already saved it keep seeing it in their Wardrobe — they chose to save it.'
          : 'It becomes eligible to appear in discovery again, provided the crawler still lists it as available.'}
        confirmLabel={moderating?.hidden ? 'Hide product' : 'Re-enable product'}
        onConfirm={moderate}
        onClose={() => setModerating(null)}
      />
    </div>
  );
}
